/**
 * ThemedButton Component
 * 
 * Button that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics and interactions.
 */

import React, { forwardRef, useState } from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedButtonProps } from '../../types/immersive-theme.types';
import {
  neumorphismButton,
  neumorphismButtonHover,
  neumorphismButtonActive,
  neumorphismButtonPrimary,
  neumorphismButtonDisabled,
} from './theme-styles/neumorphism';
import {
  brutalistButton,
  brutalistButtonHover,
  brutalistButtonActive,
  brutalistButtonPrimary,
  brutalistButtonDanger,
  brutalistButtonDisabled,
} from './theme-styles/brutalist';
import {
  retroTerminalButton,
  retroTerminalButtonHover,
  retroTerminalButtonActive,
  retroTerminalButtonPrimary,
  retroTerminalButtonDanger,
  retroTerminalButtonDisabled,
} from './theme-styles/retro-terminal';
import {
  organicNatureButton,
  organicNatureButtonHover,
  organicNatureButtonActive,
  organicNatureButtonSecondary,
  organicNatureButtonGhost,
  organicNatureButtonDanger,
  organicNatureButtonDisabled,
} from './theme-styles/organic-nature';
import {
  luxuryGoldButton,
  luxuryGoldButtonHover,
  luxuryGoldButtonActive,
  luxuryGoldButtonSecondary,
  luxuryGoldButtonSecondaryHover,
  luxuryGoldButtonGhost,
  luxuryGoldButtonDanger,
  luxuryGoldButtonDisabled,
} from './theme-styles/luxury-gold';

// Base button styles per theme
const buttonStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: '#3b82f6',
    color: '#ffffff',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismButton,
  [ImmersiveThemeType.BRUTALIST]: brutalistButton,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalButton,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureButton,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldButton,
};

// Hover styles per theme
const hoverStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { background: '#2563eb' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismButtonHover,
  [ImmersiveThemeType.BRUTALIST]: brutalistButtonHover,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalButtonHover,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureButtonHover,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldButtonHover,
};

// Active/pressed styles per theme
const activeStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { background: '#1d4ed8' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismButtonActive,
  [ImmersiveThemeType.BRUTALIST]: brutalistButtonActive,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalButtonActive,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureButtonActive,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldButtonActive,
};

// Disabled styles per theme
const disabledStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { opacity: 0.5, cursor: 'not-allowed' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismButtonDisabled,
  [ImmersiveThemeType.BRUTALIST]: brutalistButtonDisabled,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalButtonDisabled,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureButtonDisabled,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldButtonDisabled,
};

// Variant styles per theme
const getVariantStyles = (
  theme: ImmersiveThemeType,
  variant: string
): React.CSSProperties => {
  const variantMap: Record<ImmersiveThemeType, Record<string, React.CSSProperties>> = {
    [ImmersiveThemeType.NONE]: {
      primary: { background: '#3b82f6', color: '#ffffff' },
      secondary: { background: '#6b7280', color: '#ffffff' },
      ghost: { background: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6' },
      danger: { background: '#ef4444', color: '#ffffff' },
      success: { background: '#22c55e', color: '#ffffff' },
      warning: { background: '#f59e0b', color: '#ffffff' },
    },
    [ImmersiveThemeType.NEUMORPHISM]: {
      primary: neumorphismButtonPrimary,
      secondary: { background: 'var(--neu-bg-secondary)', color: 'var(--neu-text)' },
      ghost: { background: 'transparent', boxShadow: 'none', color: 'var(--neu-primary)' },
      danger: { background: 'var(--neu-danger)', color: '#ffffff' },
      success: { background: 'var(--neu-success)', color: '#ffffff' },
      warning: { background: 'var(--neu-warning)', color: '#333333' },
    },
    [ImmersiveThemeType.BRUTALIST]: {
      primary: brutalistButtonPrimary,
      secondary: { background: 'var(--brut-bg-secondary)', color: 'var(--brut-text)' },
      ghost: { background: 'transparent', boxShadow: 'none' },
      danger: brutalistButtonDanger,
      success: { background: 'var(--brut-accent-green)', color: 'var(--brut-text)' },
      warning: { background: 'var(--brut-accent-yellow)', color: 'var(--brut-text)' },
    },
    [ImmersiveThemeType.RETRO_TERMINAL]: {
      primary: retroTerminalButtonPrimary,
      secondary: { background: 'var(--term-bg-secondary)', color: 'var(--term-text)' },
      ghost: { background: 'transparent', border: 'var(--term-border-dim)' },
      danger: retroTerminalButtonDanger,
      success: { color: '#00ff00', borderColor: '#00ff00', textShadow: '0 0 10px #00ff00' },
      warning: { color: '#ffff00', borderColor: '#ffff00', textShadow: '0 0 10px #ffff00' },
    },
    [ImmersiveThemeType.ORGANIC_NATURE]: {
      primary: {},
      secondary: organicNatureButtonSecondary,
      ghost: organicNatureButtonGhost,
      danger: organicNatureButtonDanger,
      success: { background: 'var(--org-success)', color: '#ffffff' },
      warning: { background: 'var(--org-warning)', color: 'var(--org-text)' },
    },
    [ImmersiveThemeType.LUXURY_GOLD]: {
      primary: {},
      secondary: luxuryGoldButtonSecondary,
      ghost: luxuryGoldButtonGhost,
      danger: luxuryGoldButtonDanger,
      success: { background: 'var(--lux-success)', color: 'var(--lux-bg)' },
      warning: { background: 'var(--lux-warning)', color: 'var(--lux-bg)' },
    },
  };

  return variantMap[theme][variant] || {};
};

// Size styles
const sizeStyles: Record<string, React.CSSProperties> = {
  sm: { padding: '8px 16px', fontSize: '12px' },
  md: { padding: '12px 24px', fontSize: '14px' },
  lg: { padding: '16px 32px', fontSize: '16px' },
  xl: { padding: '20px 40px', fontSize: '18px' },
};

export const ThemedButton = forwardRef<HTMLButtonElement, ThemedButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      onClick,
      disabled = false,
      loading = false,
      loadingText,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      type = 'button',
      form,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const { theme, reducedMotion } = useImmersiveTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);

    const isDisabled = disabled || loading;

    // Compute styles
    const computedStyles: React.CSSProperties = {
      ...buttonStyles[theme],
      ...sizeStyles[size],
      ...getVariantStyles(theme, variant),
      ...(isHovered && !isDisabled ? hoverStyles[theme] : {}),
      ...(isActive && !isDisabled ? activeStyles[theme] : {}),
      ...(isDisabled ? disabledStyles[theme] : {}),
      ...(fullWidth ? { width: '100%' } : {}),
      ...(reducedMotion ? { transition: 'none' } : {}),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      position: 'relative',
      overflow: 'hidden',
    };

    // Loading spinner styles
    const spinnerStyles: React.CSSProperties = {
      width: '16px',
      height: '16px',
      border: '2px solid transparent',
      borderTopColor: 'currentColor',
      borderRadius: '50%',
      animation: reducedMotion ? 'none' : 'spin 0.8s linear infinite',
    };

    // Shimmer overlay for LUXURY_GOLD
    const shimmerOverlayStyles: React.CSSProperties = {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'var(--lux-shimmer)',
      backgroundSize: 'var(--lux-shimmer-size)',
      animation: reducedMotion ? 'none' : 'var(--lux-shimmer-animation)',
      pointerEvents: 'none',
    };

    return (
      <button
        ref={ref}
        type={type}
        form={form}
        onClick={onClick}
        disabled={isDisabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsActive(false);
        }}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        style={computedStyles}
        className={className}
        aria-busy={loading}
        {...rest}
      >
        {/* Shimmer effect for LUXURY_GOLD primary buttons */}
        {theme === ImmersiveThemeType.LUXURY_GOLD && variant === 'primary' && !isDisabled && (
          <span style={shimmerOverlayStyles} aria-hidden="true" />
        )}
        
        {/* Loading spinner */}
        {loading && (
          <span style={spinnerStyles} aria-hidden="true">
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </span>
        )}
        
        {/* Icon left */}
        {icon && iconPosition === 'left' && !loading && (
          <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
        )}
        
        {/* Button text */}
        <span style={{ position: 'relative', zIndex: 1 }}>
          {loading && loadingText ? loadingText : children}
        </span>
        
        {/* Icon right */}
        {icon && iconPosition === 'right' && !loading && (
          <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
        )}
      </button>
    );
  }
);

ThemedButton.displayName = 'ThemedButton';

export default ThemedButton;
